/**
 * Plain-language wording for the stylist screens. The API speaks in enums
 * (`in_progress`, `stylist_created`, `liked`); staff read sentences.
 */

import type { MemberGarmentSummary } from '@closet-theory/api';

export const NOT_PROVIDED = 'Not provided yet';

/** `kurta_set` -> `Kurta set`. */
export function humanize(value: string | null | undefined): string {
  const words = (value ?? '').replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/**
 * A member's own answer, worded for a stylist. Onboarding stores option
 * labels ("Every Few Months", "₹2,000 - ₹5,000") and some ids (`menswear`):
 * an id is humanized, a label is shown exactly as the member chose it.
 */
export function wording(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(trimmed) ? humanize(trimmed) : trimmed;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1 ? [parts[0]?.[0], parts[parts.length - 1]?.[0]] : [parts[0]?.[0]];
  return letters.filter(Boolean).join('').toUpperCase() || '?';
}

export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'Unknown time';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'Unknown time';
  const minutes = Math.round((now.getTime() - then) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(then).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return 'Unknown date';
  const then = Date.parse(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(then)) return 'Unknown date';
  return new Date(then).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(iso.length === 10 ? { timeZone: 'UTC' } : {}),
  });
}

export function accountStatus(status: string | null | undefined): string {
  if (status === 'active') return 'Active member';
  return humanize(status) || 'Status unknown';
}

export function garmentName(garment: Pick<MemberGarmentSummary, 'title' | 'category'>): string {
  return garment.title?.trim() || humanize(garment.category) || 'Untitled garment';
}

export function garmentStatus(status: string): string | undefined {
  if (status === 'active') return undefined;
  if (status === 'processing' || status === 'pending' || status === 'pending_review') {
    return 'Still being processed';
  }
  if (status === 'archived') return 'Archived by the member';
  if (status === 'failed') return 'Processing failed';
  return humanize(status);
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  colour_primary_name: 'Colour',
  colour: 'Colour',
  pattern: 'Pattern',
  fabric: 'Fabric',
  formality: 'Formality',
  season: 'Season',
  brand: 'Brand',
  size: 'Size',
  fit: 'Fit',
  occasion: 'Occasion',
  occasions: 'Occasions',
};

function scalar(value: unknown): string {
  if (typeof value === 'string') return humanize(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function attributeText(value: unknown): string {
  const values = Array.isArray(value) ? value.map(scalar) : [scalar(value)];
  return values.filter(Boolean).join(', ');
}

/** The descriptive attributes a stylist cares about, labelled; everything else is dropped. */
export function garmentAttributes(
  attributes: Record<string, unknown> | undefined,
): Array<[string, string]> {
  const seen = new Set<string>();
  return Object.entries(ATTRIBUTE_LABELS).flatMap(([key, label]) => {
    const text = attributeText(attributes?.[key]);
    if (!text || seen.has(label)) return [];
    seen.add(label);
    return [[label, text]];
  });
}

/** Search text for a garment: its name, category and descriptive attributes. */
export function garmentSearchText(garment: MemberGarmentSummary): string {
  return [
    garment.title,
    garment.category,
    ...garmentAttributes(garment.attributes).map(([, value]) => value),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function outfitSource(source: string): string {
  if (source === 'stylist_created') return 'Built by a stylist';
  if (source === 'member_created' || source === 'user_created') return 'Built by the member';
  if (source === 'ai_generated' || source === 'recommendation' || source === 'daily') {
    return 'Suggested automatically';
  }
  return humanize(source) || 'Unknown source';
}
